import {useState} from 'react'
import {Space} from 'antd'

import styles from './ProfileUpdates.module.scss'
import useStore from 'base/store'
import {isProjectRequest} from 'modules/project'
import {useGetNotifications, useReadNotificationsCategory} from 'common/api'
import {
  Box,
  Button,
  Text,
  Confirm,
  Empty,
  Loader,
  CheckButton,
  LibraryFolderIcon,
  CheckCircleIcon,
} from 'common/components'
import {maybePluralize} from 'common/helpers'
import {NotificationsCategory} from 'common/types'
import {Updates, ProfileConditions} from '.'

interface UpdatesCategoryProps {
  category: NotificationsCategory
  notifications: any[]
}

const UpdatesCategory = ({category, notifications}: UpdatesCategoryProps) => {
  const {mutate: readNotificationsCategory, isMutating} =
    useReadNotificationsCategory(category)

  if (!notifications.length) return null

  const handleAllCheck = () => {
    const notificationUuids = notifications.map(
      (notification) => notification.uuid
    )
    readNotificationsCategory(notificationUuids)
  }

  return (
    <Box mb={48}>
      <div className={styles.panel}>
        <Text as="h3" weight="bold">
          {maybePluralize(notifications?.length, `${category} update`)}
        </Text>
        {Boolean(notifications.length) && (
          <Confirm
            title={`Are you sure you want to read all ${category} updates?`}
            onConfirm={handleAllCheck}
          >
            <CheckButton className={styles.check} checked={isMutating} />
          </Confirm>
        )}
      </div>
      <Updates isCategoryReading={isMutating} notifications={notifications} />
    </Box>
  )
}

const CompletedUpdates = () => {
  const [isOpen, setOpen] = useState(false)
  const {data: completedNotifications} = useGetNotifications(true, isOpen)

  const handleToggle = () => setOpen((state) => !state)

  return (
    <>
      <Text as="h3" weight="bold" mb={isOpen ? 18 : 0}>
        <Space>
          <LibraryFolderIcon />
          <p>
            {isOpen
              ? maybePluralize(
                  completedNotifications?.length,
                  'completed update'
                )
              : 'Completed updates'}
          </p>
          <Button variant="secondary" onClick={handleToggle}>
            {isOpen ? 'hide' : 'show'}
          </Button>
        </Space>
      </Text>
      {isOpen && <Updates completed notifications={completedNotifications} />}
    </>
  )
}

const CoordinatorUpdates = () => {
  const coordinatorTabKey = useStore((state) => state.coordinatorTabKey)
  const {data: notifications} = useGetNotifications()

  const categoryNotifications = notifications?.filter((notification: any) => {
    const isRequest = isProjectRequest(notification.data.project)
    return coordinatorTabKey === 'request' ? isRequest : !isRequest
  })

  return (
    <>
      {!categoryNotifications ? (
        <Loader />
      ) : !categoryNotifications.length ? (
        <Empty
          icon={<CheckCircleIcon width={40} height={40} />}
          title={`${coordinatorTabKey} updates`}
        />
      ) : (
        <UpdatesCategory
          category={coordinatorTabKey as NotificationsCategory}
          notifications={categoryNotifications}
        />
      )}
      <CompletedUpdates />
    </>
  )
}

export default function ProfileUpdates() {
  const coordinatorTabKey = useStore((state) => state.coordinatorTabKey)

  if (coordinatorTabKey === 'condition') return <ProfileConditions />

  return <CoordinatorUpdates />
}
